import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import UIkit from "uikit";
import SurveyResponseDetail from "./SurveyResponseDetail";
import { downloadSurveyResponsePdf } from "./survey-response-pdf";
import "./survey-response.css";

export default function SurveyResponseOffcanvas({ open, survey, result, loading = false, error = "", onClose, onRetry, showMessage = false, variant = "results" }) {
  const elementRef = useRef(null);
  const instanceRef = useRef(null);
  const underlyingPanelRef = useRef(null);
  const stateRef = useRef({ open, onClose });
  const exportingRef = useRef(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  stateRef.current = { open, onClose };
  useEffect(() => { setExportError(""); }, [open, result]);
  const downloadPdf = async () => {
    if (exportingRef.current) return;
    exportingRef.current = true; setExporting(true); setExportError("");
    try { await downloadSurveyResponsePdf(survey, result); }
    catch { setExportError("Unable to download the PDF. Please try again."); }
    finally { exportingRef.current = false; setExporting(false); }
  };

  useEffect(() => {
    const element = elementRef.current;
    // Other page panels are initialized by the UIkit script in index.html.
    // Share that runtime so their active-panel stack includes this response.
    const runtime = window.UIkit || UIkit;
    const instance = runtime.offcanvas(element, { container: false, stack: true, flip: true, overlay: true, mode: "slide", escClose: true, bgClose: true });
    instanceRef.current = instance;
    const hidden = (event) => {
      if (event.target !== element) return;
      // UIkit clears shared offcanvas classes when a panel closes, including those
      // used by the Guest Details panel underneath this one.
      const underneath = underlyingPanelRef.current;
      Promise.resolve().then(() => {
        if (underneath?.element.isConnected && underneath.element.classList.contains("uk-open")) {
          document.body.classList.add(...underneath.bodyClasses);
          document.documentElement.classList.add(...underneath.htmlClasses);
          document.documentElement.style.overflowY = underneath.overflowY;
          document.body.style.touchAction = underneath.touchAction;
        }
      });
      if (stateRef.current.open) stateRef.current.onClose();
    };
    // Guest Details uses the page's UIkit offcanvas instance. Consume Escape before
    // its document listener so dismissing this overlay leaves Guest Details open.
    const escape = (event) => {
      if (stateRef.current.open && event.key === "Escape") {
        event.preventDefault();
        event.stopImmediatePropagation();
        instance.hide();
      }
    };
    UIkit.util.on(element, "hidden", hidden);
    document.addEventListener("keydown", escape, true);
    return () => {
      UIkit.util.off(element, "hidden", hidden);
      document.removeEventListener("keydown", escape, true);
      instanceRef.current = null;
      Promise.resolve(instance.hide()).then(() => instance.$destroy(), () => instance.$destroy());
    };
  }, []);

  useEffect(() => {
    const instance = instanceRef.current;
    if (open) {
      const underneath = document.querySelector(".uk-offcanvas.uk-open:not(.survey-response-offcanvas)");
      underlyingPanelRef.current = underneath ? {
        element: underneath,
        bodyClasses: ["uk-offcanvas-container", "uk-offcanvas-flip", "uk-offcanvas-container-animation"].filter((name) => document.body.classList.contains(name)),
        htmlClasses: ["uk-offcanvas-page"].filter((name) => document.documentElement.classList.contains(name)),
        overflowY: document.documentElement.style.overflowY,
        touchAction: document.body.style.touchAction,
      } : null;
      instance.show();
    }
    else if (elementRef.current.classList.contains("uk-open")) instance.hide();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    document.body.classList.add("survey-response-print-active");
    document.documentElement.classList.add("survey-response-print-active");
    return () => {
      document.body.classList.remove("survey-response-print-active");
      document.documentElement.classList.remove("survey-response-print-active");
    };
  }, [open]);

  // Keep the overlay outside page tabs and offcanvas panels without letting UIkit move React-owned DOM.
  return createPortal(
    <div ref={elementRef} className="uk-offcanvas survey-response-offcanvas" role="dialog" aria-modal="true" aria-label="Survey response" aria-hidden={!open}>
      <div className="uk-offcanvas-bar uk-dark">
        <div className="survey-response-toolbar no-print">
          <div className="uk-flex uk-flex-middle survey-response-actions">
            <button type="button" className="uk-button hms-img-btn survey-response-pdf" aria-label="Download survey response PDF" title={exporting ? "Creating PDF…" : "Download PDF"} disabled={loading || !!error || !result || exporting} aria-busy={exporting} onClick={downloadPdf}>
              <span className="survey-response-icon" aria-hidden="true" style={{ "--survey-response-icon": 'url("/images/pdf-icon.svg")' }} />
            </button>
            <button type="button" className="uk-button hms-img-btn survey-response-print" aria-label="Print survey response" title="Print" disabled={loading || !!error || !result} onClick={() => window.print()}>
              <span className="survey-response-icon" aria-hidden="true" style={{ "--survey-response-icon": 'url("/images/print-no-bg.svg")' }} />
            </button>
            <button type="button" className="uk-offcanvas-close" data-uk-close aria-label="Close survey response" />
          </div>
        </div>
        {exportError && <div role="alert" className="uk-alert uk-alert-danger no-print">{exportError}</div>}
        {loading ? <div role="status" className="uk-padding-small"><span data-uk-spinner="ratio: 0.7" /> Loading survey response…</div>
          : error ? <div role="alert" className="uk-alert uk-alert-danger">{error}{onRetry && <button type="button" className="uk-button uk-button-text uk-margin-left" onClick={onRetry}>Try again</button>}</div>
          : <SurveyResponseDetail survey={survey} result={result} showMessage={showMessage} variant={variant} />}
      </div>
    </div>, document.body,
  );
}
